import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Full-app paywall shown when an org has no usable subscription (never started a
// trial, or it lapsed/cancelled). Admins can start the 7-day trial straight into
// Stripe Checkout; members are told to ask their admin. Exemptions (staff,
// billing-override, enterprise) are handled by the caller in App.js.
export default function SubscriptionGate() {
  const { supplierInfo, signOut } = useAuth();
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const isMember = supplierInfo?.role === 'member';

  const startTrial = async (tier) => {
    setLoading(tier);
    setError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/billing/checkout-session`, {
        tier,
        cadence: 'monthly',
        trial: true,
      });
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to start checkout');
      setLoading('');
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-start" style={{ minHeight: '80vh', padding: '48px 16px' }}>
      <div className="card shadow-sm" style={{ maxWidth: 560, width: '100%' }}>
        <div className="card-body p-4 p-md-5">
          {isMember ? (
            <>
              <h3 className="mb-2">Subscription required</h3>
              <p className="text-muted mb-4">
                Your workspace doesn’t have an active subscription yet. Ask an admin on your
                team to start the free trial, then you’ll have full access.
              </p>
              <button className="btn btn-outline-secondary" onClick={signOut}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <h3 className="mb-1">Start your 7 day free trial</h3>
              <p className="text-muted mb-4">
                Pick a plan to unlock Zilch. You won’t be charged until your trial ends, and
                you can cancel any time before then. 
              </p>

              {error && <div className="alert alert-danger" role="alert">{error}</div>}

              <div className="d-grid gap-3">
                <button
                  className="btn btn-dark btn-lg d-flex justify-content-between align-items-center"
                  onClick={() => startTrial('starter')}
                  disabled={!!loading}
                >
                  <span>{loading === 'starter' ? 'Starting…' : 'Start trial on Starter'}</span>
                  <span style={{ fontSize: 14, opacity: 0.7 }}>$109/mo after trial</span>
                </button>
                <button
                  className="btn btn-outline-dark btn-lg d-flex justify-content-between align-items-center"
                  onClick={() => startTrial('team')}
                  disabled={!!loading}
                >
                  <span>{loading === 'team' ? 'Starting…' : 'Start trial on Team'}</span>
                  <span style={{ fontSize: 14, opacity: 0.7 }}>$313/mo after trial</span>
                </button>
                <button
                  className="btn btn-outline-dark btn-lg d-flex justify-content-between align-items-center"
                  onClick={() => startTrial('pro')}
                  disabled={!!loading}
                >
                  <span>{loading === 'pro' ? 'Starting…' : 'Start trial on Professional'}</span>
                  <span style={{ fontSize: 14, opacity: 0.7 }}>$656/mo after trial</span>
                </button>
              </div>

              <div className="mt-4 d-flex align-items-center justify-content-between">
                <small className="text-muted">A card is required to start your trial. </small>
                <small>
                 <a href="/billing" target="_blank" rel="noopener noreferrer">View pricing information</a>
                </small>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
